import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

const CarouselSection = styled.section`
  margin-top: 2rem;
`;

const Viewport = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const Slides = styled.div`
  display: flex;
  transition: transform 600ms ease;
  transform: translateX(${({ activeIndex }) => `-${activeIndex * 100}%`});
`;

const Slide = styled.div`
  min-width: 100%;
  position: relative;
  background: ${({ fallback }) => fallback};
  cursor: pointer;
`;

const BannerImage = styled.img`
  width: 100%;
  height: clamp(320px, 60vw, 520px);
  object-fit: cover;
  display: block;
`;

const ContentPanel = styled.div`
  position: absolute;
  top: 50%;
  right: clamp(1rem, 4vw, 3rem);
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.94);
  border-radius: 1.5rem;
  padding: clamp(1rem, 3vw, 2rem);
  width: min(320px, 45%);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  pointer-events: none;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin: 1rem;
    width: auto;
  }
`;

const TitleText = styled.span`
  white-space: nowrap;
  
  @media (max-width: 768px) {
    white-space: normal;
    display: block;
  }
`;

const Eyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Badge = styled.span`
  align-self: flex-start;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 700;
  padding: 0.45rem 1rem;
  border-radius: 999px;
  font-size: 0.85rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const ControlButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.md};
  cursor: pointer;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    transform: translateY(-2px);
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.surfaceMuted)};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  padding: 0;
`;

const heroSlides = [
  {
    id: 'lower-elementary',
    image: '/assets/hero/hero-lower-elementary.png',
    alt: '초등 저학년부 학습 배너',
    badge: '초등 저학년부',
    fallback: 'linear-gradient(135deg, #ffe9c6 0%, #fff6e7 100%)',
    duration: 9000,
    route: '/programs/elementary',
  },
  {
    id: 'upper-elementary',
    image: '/assets/hero/hero-upper-elementary.png',
    alt: '초등 고학년부 학습 배너',
    badge: '초등 고학년부',
    fallback: 'linear-gradient(135deg, #ffe3ec 0%, #fff8f3 100%)',
    route: '/programs/elementary',
  },
  {
    id: 'middle',
    image: '/assets/hero/hero-middle.png',
    alt: '중등부 학습 배너',
    badge: '중등부',
    fallback: 'linear-gradient(135deg, #cfd9ff 0%, #f5f7ff 100%)',
    route: '/programs/middle',
  },
  {
    id: 'high',
    image: '/assets/hero/hero-high.png',
    alt: '고등부 학습 배너',
    badge: '고등부',
    fallback: 'linear-gradient(135deg, #d7e9ff 0%, #f0f5ff 100%)',
    route: '/programs/high',
  },
  {
    id: 'adult',
    image: '/assets/hero/hero-adult.png',
    alt: '성인 취업 준비 배너',
    badge: '성인 / 취업준비',
    fallback: 'linear-gradient(135deg, #f8e3d1 0%, #fef1e9 100%)',
    route: '/programs/adult',
  },
];

const DEFAULT_AUTOPLAY_DELAY = 6000;

const HeroCarousel = () => {
  const history = useHistory();
  const slides = useMemo(() => heroSlides, []);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, slides[active]?.duration || DEFAULT_AUTOPLAY_DELAY);

    return () => clearTimeout(timeout);
  }, [active, slides]);

  const goTo = (index) => {
    setActive((index + slides.length) % slides.length);
  };

  const handleSlideClick = (slide) => {
    if (slide.route) {
      history.push(slide.route);
    }
  };

  return (
    <CarouselSection>
      <Viewport>
        <Slides activeIndex={active}>
          {slides.map((slide) => (
            <Slide key={slide.id} fallback={slide.fallback} onClick={() => handleSlideClick(slide)}>
              <BannerImage src={slide.image} alt={slide.alt} loading="lazy" />
              <ContentPanel>
                <Eyebrow>Code the Future!</Eyebrow>
                <Title>
                  <TitleText>에이스</TitleText>
                  {' '}
                  <TitleText>코딩학원</TitleText>
                </Title>
                <Badge>{slide.badge}</Badge>
              </ContentPanel>
            </Slide>
          ))}
        </Slides>
      </Viewport>
      <Controls>
        <ControlButton type="button" aria-label="Previous slide" onClick={() => goTo(active - 1)}>
          ‹
        </ControlButton>
        <Dots>
          {slides.map((slide, index) => (
            <Dot
              key={slide.id}
              type="button"
              aria-label={`${slide.badge} 배너 보기`}
              active={index === active}
              onClick={() => goTo(index)}
            />
          ))}
        </Dots>
        <ControlButton type="button" aria-label="Next slide" onClick={() => goTo(active + 1)}>
          ›
        </ControlButton>
      </Controls>
    </CarouselSection>
  );
};

export default HeroCarousel;

